import { useCallback, useEffect, useState } from "react";
import { useApi } from "../../api";
import { PageHeader } from "../GenericPage";
import { n0, n2 } from "../../lib/format";
import { Notice, Stat } from "../../lib/ui";

/*
 * Service payments report – port of service_payments_report.php (API: GET /api/services/payments-report)
 * Defaults to this month. Filters: dates, client name, method, service. Summary, by-service and by-method
 * breakdowns, and every payment with its Open / Closed (day-end) status. Print-friendly.
 */

const fmtDt = (s) => (s ? new Date(s.replace(" ", "T")).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "–");

export default function ServicePaymentsReport() {
  const api = useApi();
  const [f, setF] = useState({ date_from: "", date_to: "", client: "", method: "", service: "" });
  const [form, setForm] = useState(f);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(async (q) => {
    try { const d = await api.get("/api/services/payments-report", q); setData(d); setForm(d.filters); setError(null); }
    catch (e) { setError(e.message); }
  }, [api]);
  useEffect(() => { load(f); }, [load, f]);
  const s = data?.summary;
  const maxSvc = Math.max(1, ...(data?.by_service || []).map((r) => r.svc_total));

  return (
    <main className="pg" style={{ maxWidth: "88rem" }}>
      <PageHeader slug="service_payments_report" actions={<button type="button" className="btn" onClick={() => window.print()}><i className="fas fa-print" /> Print</button>} />
      <Notice notice={error && { tone: "err", text: error }} />
      <div className="panel no-print" style={{ marginBottom: "1.25rem" }}>
        <form className="toolbar" style={{ borderBottom: 0 }} onSubmit={(e) => { e.preventDefault(); setF({ ...form }); }}>
          <div className="field"><label htmlFor="sr-f">From</label><input id="sr-f" type="date" value={form.date_from} onChange={(e) => setForm({ ...form, date_from: e.target.value })} /></div>
          <div className="field"><label htmlFor="sr-t">To</label><input id="sr-t" type="date" value={form.date_to} onChange={(e) => setForm({ ...form, date_to: e.target.value })} /></div>
          <div className="field grow"><label htmlFor="sr-c">Client</label><input id="sr-c" value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })} placeholder="Client name" /></div>
          <div className="field"><label htmlFor="sr-m">Method</label>
            <select id="sr-m" value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })}>
              <option value="">All methods</option>{(data?.options.methods || []).map((m) => <option key={m}>{m}</option>)}</select></div>
          <div className="field"><label htmlFor="sr-s">Service</label>
            <select id="sr-s" value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })}>
              <option value="">All services</option>{(data?.options.services || []).map((x) => <option key={x.service_id} value={x.service_id}>{x.service_name}</option>)}</select></div>
          <button type="submit" className="btn primary">Apply</button>
          <button type="button" className="btn" title="Reset" onClick={() => setF({ date_from: "", date_to: "", client: "", method: "", service: "" })}><i className="fas fa-rotate-left" /></button>
        </form>
      </div>
      {data && <p className="print-only" style={{ fontSize: ".8rem" }}>Service payments, {data.filters.date_from} to {data.filters.date_to}</p>}
      {s && (
        <div className="ui-stats">
          <Stat icon="fa-coins" tone="up" label={`Total revenue · ${n0(s.total_txn)} payments`} value={n0(s.grand_total)} unit="TZS" />
          <Stat icon="fa-money-bill-wave" label={`Cash · non-cash ${n0(s.non_cash_total)}`} value={n0(s.cash_total)} unit="TZS" />
          <Stat icon="fa-users" tone="muted" label={`Clients · ${n0(s.unique_services)} services`} value={n0(s.unique_clients)} />
          <Stat icon="fa-chart-simple" tone="warn" label={`Average · largest ${n0(s.max_amount)}`} value={n0(s.avg_amount)} unit="TZS" />
        </div>
      )}
      {data && (data.by_service.length > 0 || data.by_method.length > 0) && (
        <div className="sr-breakdowns">
          <section className="panel" style={{ padding: "1rem 1.25rem" }}>
            <h3 className="tb-h3" style={{ fontSize: ".95rem", margin: "0 0 .8rem" }}>By service</h3>
            {data.by_service.map((r) => (
              <div key={r.service_name || "none"} className="sr-bar">
                <div className="sr-bar-top"><span><b>{r.service_name || "Deleted service"}</b> <small>{r.category}</small></span><span className="mono">{n0(r.svc_total)}</span></div>
                <div className="sr-track"><div style={{ width: `${(r.svc_total / maxSvc) * 100}%` }} /></div>
                <small>{r.txn_count} payment{r.txn_count === 1 ? "" : "s"} · average {n0(r.svc_avg)}</small>
              </div>
            ))}
          </section>
          <section className="panel" style={{ padding: "1rem 1.25rem" }}>
            <h3 style={{ fontSize: ".95rem", margin: "0 0 .8rem" }}>By method</h3>
            <table className="tbl"><tbody>{data.by_method.map((m) => (
              <tr key={m.payment_method}><td>{m.payment_method}</td><td className="num mono">{m.txn_count}</td><td className="num mono">{n2(m.meth_total)}</td></tr>))}</tbody></table>
          </section>
        </div>
      )}
      <section className="panel">
        <div className="tbl-wrap">
          <table className="tbl">
            <thead><tr><th>#</th><th>Date and time</th><th>Client</th><th>Service</th><th>Category</th><th>Method</th><th>Reference</th><th>Status</th><th className="num">Amount (TZS)</th></tr></thead>
            <tbody>
              {data?.rows.map((r) => (
                <tr key={r.payment_id}>
                  <td className="mono">{r.payment_id}</td><td style={{ whiteSpace: "nowrap" }}>{fmtDt(r.paid_at)}</td><td style={{ fontWeight: 600, color: "var(--ink)" }}>{r.client_name}</td>
                  <td>{r.service_name || "–"}</td><td>{r.category || "–"}</td><td>{r.payment_method}</td><td className="mono">{r.reference_number || "–"}</td>
                  <td><span className={`pill-tag ${r.closed ? "" : "warn"}`}><i className={`fas ${r.closed ? "fa-lock" : "fa-clock"}`} /> {r.closed ? "Closed" : "Open"}</span></td>
                  <td className="num mono">{n2(r.amount)}</td>
                </tr>
              ))}
            </tbody>
            {data?.rows.length > 0 && <tfoot><tr><td colSpan={8}>Total</td><td className="num mono">{n2(s.grand_total)}</td></tr></tfoot>}
          </table>
          {data && data.rows.length === 0 && <div className="empty"><i className="fas fa-inbox" />No service payments for these filters.</div>}
        </div>
      </section>
    </main>
  );
}
